import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { getData } from "../../../FetchApiServices";

NewRequestModal.propTypes = {
  onClose:     PropTypes.func.isRequired,
  autoCode:    PropTypes.string,
  extraParams: PropTypes.object,
};

export default function NewRequestModal({ onClose, autoCode = null, extraParams = {} }) {
  const navigate = useNavigate();

  const [workflows,    setWorkflows]    = useState([]);
  const [requestTypes, setRequestTypes] = useState([]);
  const [step,         setStep]         = useState(1);
  const [pickedType,   setPickedType]   = useState(null);
  const [loading,      setLoading]      = useState(true);

  const init = useCallback(async () => {
    setLoading(true);
    const [wfResp, rtResp] = await Promise.all([
      getData("t2t/workflows?page=1&limit=100"),
      getData("t2t/request-types"),
    ]);
    let wfList = [];
    let rtList = [];
    if (!wfResp.error) {
      const raw = wfResp.data?.data;
      const list = Array.isArray(raw) ? raw
        : Array.isArray(raw?.data)      ? raw.data
        : Array.isArray(raw?.rows)      ? raw.rows
        : Array.isArray(raw?.workflows) ? raw.workflows
        : [];
      wfList = list.filter((w) => {
        const s = w.workflow_status || w.status;
        return s === "active" || s === "published";
      });
      setWorkflows(wfList);
    }
    if (!rtResp.error) {
      const raw = rtResp.data?.data;
      rtList = Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : [];
      rtList = rtList.filter((rt) => rt.status === 1);
      setRequestTypes(rtList);
    }
    setLoading(false);
    if (autoCode) {
      const matchRT = rtList.find((rt) => rt.code === autoCode);
      if (matchRT) {
        const wfs = wfList.filter((wf) =>
          (Array.isArray(wf.request_type_ids) && wf.request_type_ids.includes(matchRT.request_type_id)) ||
          wf.request_type_id === matchRT.request_type_id
        );
        const qs = new URLSearchParams(extraParams).toString();
        const url = (wfId) => `/t2t-submit/${wfId}${qs ? `?${qs}` : ""}`;
        if (wfs.length === 1) { onClose(); navigate(url(wfs[0].workflow_id)); return; }
        if (wfs.length > 1)  { setPickedType(matchRT); setStep(2); }
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { init(); }, [init]);

  // Check both junction-table ids (multi-select) and legacy single request_type_id
  const wfHasType = (wf, rtId) =>
    (Array.isArray(wf.request_type_ids) && wf.request_type_ids.includes(rtId)) ||
    wf.request_type_id === rtId;
  const workflowsFor = (rtId) => workflows.filter((wf) => wfHasType(wf, rtId));
  const orphans      = workflows.filter((wf) =>
    !wf.request_type_id &&
    (!Array.isArray(wf.request_type_ids) || wf.request_type_ids.length === 0)
  );

  const pickType = (rt) => {
    const wfs = rt === "__other__" ? orphans : workflowsFor(rt.request_type_id);
    if (wfs.length === 0) return;
    if (wfs.length === 1) { onClose(); navigate(`/t2t-submit/${wfs[0].workflow_id}`); return; }
    setPickedType(rt);
    setStep(2);
  };

  const workflowsToShow = pickedType === "__other__"
    ? orphans
    : pickedType ? workflowsFor(pickedType.request_type_id) : [];

  return (
    <div className="t2t-overlay" onClick={onClose}>
      <div className="t2t-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 560 }}>
        <div className="t2t-modal-head">
          <div>
            <div className="t2t-modal-title">
              New Request
              {step === 2 && pickedType && pickedType !== "__other__" && (
                <span style={{ fontSize: 12, fontWeight: 500, color: "var(--t2t-text-muted)", marginLeft: 8 }}>
                  — {pickedType.name}
                </span>
              )}
            </div>
            <div className="t2t-modal-sub">
              {step === 1 ? "What kind of request are you making?" : "Pick a workflow to process this request."}
            </div>
          </div>
          <button className="t2t-close-btn" onClick={onClose}>×</button>
        </div>

        <div className="t2t-modal-body">
          {loading ? (
            <div style={{ padding: 32, textAlign: "center", fontSize: 12, color: "var(--t2t-text-muted)" }}>Loading…</div>
          ) : step === 1 ? (
            <>
              {requestTypes.length === 0 && orphans.length === 0 ? (
                <div style={{ padding: 24, textAlign: "center", color: "var(--t2t-text-muted)", fontSize: 13 }}>
                  No request types or workflows available. Publish a workflow first.
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {requestTypes.map((rt) => {
                    const wfs = workflowsFor(rt.request_type_id);
                    const disabled = wfs.length === 0;
                    return (
                      <div
                        key={rt.request_type_id}
                        className="t2t-list-row"
                        style={{ cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.5 : 1 }}
                        onClick={() => pickType(rt)}
                        title={disabled ? "No workflow attached yet." : ""}
                      >
                        <div className="t2t-list-icon" style={{ background: "#EFF6FF" }}>📋</div>
                        <div style={{ flex: 1 }}>
                          <div className="t2t-list-title">
                            {rt.name}
                            {rt.is_system === 1 && (
                              <span style={{ marginLeft: 6, fontSize: 9, fontWeight: 700, color: "#1B3A6B", background: "#DBEAFE", padding: "1px 6px", borderRadius: 4 }}>SYSTEM</span>
                            )}
                          </div>
                          <div className="t2t-list-meta">
                            {rt.description || rt.category || ""}
                            {rt.description && rt.category ? ` · ${rt.category}` : ""}
                          </div>
                          <div style={{ fontSize: 10, color: "var(--t2t-text-muted)", marginTop: 2 }}>
                            {wfs.length === 0
                              ? "No workflow attached"
                              : wfs.length === 1
                                ? <span>↳ {wfs[0].name}</span>
                                : `${wfs.length} workflows`}
                          </div>
                        </div>
                        <span style={{ fontSize: 16, color: disabled ? "#CBD5E1" : "var(--t2t-primary)" }}>→</span>
                      </div>
                    );
                  })}
                  {orphans.length > 0 && (
                    <div
                      className="t2t-list-row"
                      style={{ cursor: "pointer", borderTop: "1px dashed var(--t2t-border)", marginTop: 4, paddingTop: 14 }}
                      onClick={() => pickType("__other__")}
                    >
                      <div className="t2t-list-icon" style={{ background: "#F1F5F9" }}>📁</div>
                      <div style={{ flex: 1 }}>
                        <div className="t2t-list-title">Other / Uncategorised</div>
                        <div className="t2t-list-meta">Workflows without a request type attached.</div>
                        <div style={{ fontSize: 10, color: "var(--t2t-text-muted)", marginTop: 2 }}>
                          {orphans.length} workflow{orphans.length > 1 ? "s" : ""}
                        </div>
                      </div>
                      <span style={{ fontSize: 16, color: "var(--t2t-primary)" }}>→</span>
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {workflowsToShow.map((wf) => (
                <div
                  key={wf.workflow_id}
                  className="t2t-list-row"
                  style={{ cursor: "pointer" }}
                  onClick={() => { onClose(); navigate(`/t2t-submit/${wf.workflow_id}`); }}
                >
                  <div className="t2t-list-icon" style={{ background: "#F0FDF4" }}>📋</div>
                  <div style={{ flex: 1 }}>
                    <div className="t2t-list-title">{wf.name}</div>
                    {wf.description && <div className="t2t-list-meta">{wf.description}</div>}
                  </div>
                  <span style={{ fontSize: 16, color: "var(--t2t-primary)" }}>→</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="t2t-modal-foot">
          {step === 2
            ? <button className="t2t-btn t2t-btn-ghost" onClick={() => { setStep(1); setPickedType(null); }}>← Back</button>
            : <span />
          }
          <button className="t2t-btn t2t-btn-ghost" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}
